import React, { useContext, useMemo, useState } from "react";
import Select from "react-select";
import { FaUserTie } from "react-icons/fa";
import appContext from "../../../../context/app/appContext";

const StaffAttendanceSelector = ({ data, onChange }) => {
  const { allStaff } = useContext(appContext);
  const staffState = data.staff || { organiser: null, attending: [] };

  const [staffError, setStaffError] = useState("");

  const staffOptions = useMemo(() => {
    return (allStaff || [])
      .filter((s) => s.staffEmpCode)
      .sort(
        (a, b) =>
          a.staffSurname.localeCompare(b.staffSurname) ||
          a.staffGvnName.localeCompare(b.staffGvnName)
      )
      .map((s) => ({
        value: s.staffEmpCode,
        label: `${s.staffGvnName} ${s.staffSurname}`,
        email: s.staffEmail,
        staffCode: s.staffCode,
        original: s,
      }));
  }, [allStaff]);

  const formatOptionLabel = ({ label, email }) => (
    <div className="flex flex-col">
      <span className="flex items-center gap-2">
        <FaUserTie className="text-gray-500" />
        {label}
      </span>
      {email && <span className="text-xs text-gray-500 ml-6">{email}</span>}
    </div>
  );

  const handleAttendingChange = (selected) => {
    onChange("staff", {
      organiser: staffState.organiser,
      attending: selected || [],
    });
  };

  const toggleStaffUnknown = (checked) => {
    onChange("staff", {
      ...staffState,
      staffUnknown: checked,
      attending: checked ? [] : staffState.attending,
      staffUnknownDetails: checked
        ? {
            approxNumStaff: "",
            staffDescription: "",
            ...staffState.staffUnknownDetails,
          }
        : { approxNumStaff: "", staffDescription: "" },
    });
  };

  const handleUnknownDetailsChange = (field, value) => {
    if (field === "approxNumStaff") {
      // Allow only empty or numeric input
      if (!/^\d*$/.test(value)) {
        setStaffError("Please enter a valid number");
        return;
      }

      const numericValue = parseInt(value, 10);
      if (value && numericValue < 1) {
        setStaffError("Number must be at least 1");
        return;
      }

      setStaffError("");
    }

    onChange("staff", {
      ...staffState,
      staffUnknownDetails: {
        ...staffState.staffUnknownDetails,
        [field]: value,
      },
    });
  };

  return (
    <div className="space-y-4">
      {!staffState.staffUnknown && (
        <div>
          <label className="block font-medium text-gray-700 mb-1">
            Staff in Attendance
          </label>
          <Select
            isMulti
            options={staffOptions}
            value={staffState.attending}
            onChange={handleAttendingChange}
            placeholder="Select additional staff..."
            formatOptionLabel={formatOptionLabel}
          />
        </div>
      )}

      <div className="space-y-2">
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={staffState.staffUnknown || false}
            onChange={(e) => toggleStaffUnknown(e.target.checked)}
          />
          Staff not yet confirmed
        </label>

        {staffState.staffUnknown && (
          <div className="border border-blue-100 bg-blue-50 rounded-md p-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Approximate number of staff
              </label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                className={`w-full border rounded-md px-3 py-2 ${
                  staffError ? "border-red-500" : "border-gray-300"
                }`}
                value={staffState.staffUnknownDetails?.approxNumStaff || ""}
                onChange={(e) =>
                  handleUnknownDetailsChange("approxNumStaff", e.target.value)
                }
              />
              {staffError && (
                <p className="text-red-600 text-sm mt-1">{staffError}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description of staff attending
              </label>
              <textarea
                rows={3}
                className="w-full border rounded-md px-3 py-2"
                placeholder="E.g. Subject teachers, sports coaches, camp facilitators"
                value={staffState.staffUnknownDetails?.staffDescription || ""}
                onChange={(e) =>
                  handleUnknownDetailsChange("staffDescription", e.target.value)
                }
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StaffAttendanceSelector;
